import type { SbBlokData } from "@storyblok/react";
import { StoryblokComponent, useStoryblokState } from "@storyblok/react";
import type {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from "next";

import Footer from "@/components/Layout/Footer";
import Header from "@/components/Layout/Header";
import NewsletterSignup from "@/components/NewsletterSignup";
import NotFound from "@/components/NotFound";
import config from "@/config";
import { fetchServerTranslations } from "@/i18n.server";
import useStore from "@/lib/stateManagement/store";
import getPaths from "@/lib/storyblok/getPaths";
import getStory from "@/lib/storyblok/getStory";

const PrimaryNavigation: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ story: initialStory, preview }) => {
  const story = useStoryblokState(initialStory!, {});
  const isPreview = useStore((state) => state.preview) || preview;

  if (!story?.content) {
    return <NotFound />;
  }

  if (!isPreview) {
    return <></>;
  }

  if (story.content.component === "primary-navigation") {
    return (
      <Header previewData={{ [story.content.component]: story.content }} />
    );
  }
  if (story.content.component === "secondary-navigation") {
    return (
      <Header previewData={{ [story.content.component]: story.content }} />
    );
  }
  if (story.content.component === "mobile-navigation") {
    return (
      <Header previewData={{ [story.content.component]: story.content }} />
    );
  }

  if (story.content.component === "footer") {
    return (
      <Footer previewData={{ [story.content.component]: story.content }} />
    );
  }

  if (story.content.component === "newsletter-signup") {
    return (
      <NewsletterSignup
        previewData={{ [story.content.component]: story.content }}
      />
    );
  }

  return <StoryblokComponent blok={story.content} />;
};

export default PrimaryNavigation;

export const getStaticProps = async ({
  params,
  preview = false,
  locale = "en",
}: GetStaticPropsContext) => {
  let notFound = false;
  if (!preview) {
    notFound = true;
  }
  const slug = params?.static as string;
  const story = await getStory<SbBlokData>({
    slug,
    preview,
    folder: "statics",
    locale,
  });

  return {
    notFound,
    props: {
      ...(await fetchServerTranslations(locale)),
      story,
      preview,
    },
    revalidate: config.revalidate,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getPaths({
    params: "static",
    folder: "statics",
  });

  return {
    paths,
    fallback: "blocking",
  };
};
